import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { UserApiService } from '../../shared/services/api/user-api.service';
import { PricingActionEnablementService } from '../tenant/pricing-action-enablement.service';
import { TenantContextService } from '../tenant/tenant-context.service';
import { TenantLifecycleService } from '../tenant/tenant-lifecycle.service';
import {
  CONFIGURATION_NAV_FLAG_KEY,
  FeatureFlagService,
  WEEKLY_DIGEST_ENABLED_FLAG_KEY,
} from '../feature-flags/feature-flag.service';
import { visibleAdminRoutes } from './admin-tool-visibility';

/**
 * Guards `/admin` with the rail's own visibility rule: no tool sends the caller to `/home`,
 * exactly one goes straight to it, more open the overview.
 */
export const adminOverviewGuard: CanActivateFn = async () => {
  const userApi = inject(UserApiService);
  const router = inject(Router);
  const flags = inject(FeatureFlagService);
  const lifecycle = inject(TenantLifecycleService);
  const tenantContext = inject(TenantContextService);
  const pricingActionEnablement = inject(PricingActionEnablementService);

  try {
    await firstValueFrom(lifecycle.bootstrapTenantContext());
    await flags.ready();

    const me = await firstValueFrom(userApi.getUserData());
    const routes = visibleAdminRoutes({
      granted: new Set(me.permissions ?? []),
      weeklyDigest: flags.isEnabledWithFallback(
        WEEKLY_DIGEST_ENABLED_FLAG_KEY,
        environment.features.weeklyDigestEnabled
      ),
      configuration: flags.isEnabled(CONFIGURATION_NAV_FLAG_KEY) && tenantContext.isPrivileged(),
      pricingActionOnly: pricingActionEnablement.pricingActionOnlyForTenant(),
    });
    return landingFor(routes, router);
  } catch {
    // Network / auth / bootstrap issues fall through to redirect, same as
    // every individual admin guard.
  }
  return router.createUrlTree(['/home']);
};

function landingFor(routes: readonly string[], router: Router): true | UrlTree {
  if (routes.length === 0) return router.createUrlTree(['/home']);
  if (routes.length === 1) return router.createUrlTree([routes[0]]);
  return true;
}
